import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Send, Mail, ArrowUpRight } from "lucide-react";
import MastodonIcon, { MASTODON_URL, MASTODON_HANDLE } from "./MastodonIcon";
import LinkedInIcon, { LINKEDIN_URL, LINKEDIN_HANDLE } from "./LinkedInIcon";
import { Turnstile } from "@marsidev/react-turnstile";
import Eyebrow from "./Eyebrow";

// Same-origin "/api" on Cloudflare; Emergent still sets REACT_APP_BACKEND_URL.
const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const LOTTIE_ENVELOPE = "/lottie/envelope-send.json";
const TURNSTILE_SITEKEY = "0x4AAAAAACv21Ti8c5CisESY";

// The envelope animation (lottie-web, the biggest library on the page) is only
// shown after a successful send, so it is downloaded then and not on page load.
const SuccessAnimation = () => {
  const [Lottie, setLottie] = useState(null);
  useEffect(() => {
    let cancelled = false;
    import("lottie-react")
      .then((m) => { if (!cancelled) setLottie(() => m.default); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return (
    <div style={{ width: 72, height: 72 }}>
      {Lottie && <Lottie path={LOTTIE_ENVELOPE} autoplay loop={false} style={{ width: 72, height: 72 }} />}
    </div>
  );
};

const ContactForm = () => {
  const [form, setForm]           = useState({ name: "", email: "", phone: "", message: "" });
  const [status, setStatus]       = useState(null);
  const [errors, setErrors]       = useState({});
  const [turnstileToken, setToken] = useState("");
  const turnstileRef              = useRef(null);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Bitte geben Sie Ihren Namen ein.";
    const hasEmail = form.email.trim().length > 0;
    const hasPhone = form.phone.trim().length > 0;
    if (!hasEmail && !hasPhone)
      e.contact = "Bitte hinterlassen Sie mindestens eine Kontaktmöglichkeit.";
    else if (hasEmail && !/\S+@\S+\.\S+/.test(form.email))
      e.email = "Ungültige E-Mail-Adresse.";
    if (!form.message.trim()) e.message = "Bitte beschreiben Sie kurz Ihr Anliegen.";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    if (!turnstileToken) { setErrors({ captcha: "Bitte bestätigen Sie, dass Sie kein Roboter sind." }); return; }
    setErrors({}); setStatus("loading");
    try {
      const resp = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, turnstile_token: turnstileToken }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setStatus("success");
      setForm({ name: "", email: "", phone: "", message: "" });
      setToken("");
    } catch {
      setStatus("error");
      if (turnstileRef.current) turnstileRef.current.reset();
      setToken("");
    }
  };

  const fieldCls = (field) =>
    `w-full px-4 py-3 rounded-lg border bg-card text-sm text-foreground
     placeholder-muted-foreground/60 focus:outline-none focus:ring-2 transition-colors
     ${errors[field] || (["email","phone"].includes(field) && errors.contact)
       ? "border-destructive focus:ring-destructive/20"
       : "border-border focus:border-accent/50 focus:ring-accent/15"}`;

  return (
    <section id="kontakt" className="py-16 sm:py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-start">

          {/* Left */}
          <div className="space-y-6">
            <div className="space-y-2">
              <Eyebrow>Kontakt</Eyebrow>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
                Interesse an mir und meinen Fähigkeiten?
              </h2>
            </div>
            <p className="text-muted-foreground text-base leading-relaxed">
              Hinterlassen Sie mir Ihre Kontaktdaten und ich sende Ihnen gerne
              meine Unterlagen zu.
            </p>
            <a
              data-testid="email-direct-link"
              href="mailto:andreas@fischinger.ch"
              className="inline-flex items-center gap-2 text-accent hover:text-accent/80
                         font-medium text-sm transition-colors"
            >
              <Mail className="size-4" strokeWidth={1.8} />
              andreas@fischinger.ch
            </a>
            <a
              data-testid="linkedin-link"
              href={LINKEDIN_URL}
              rel="noopener"
              target="_blank"
              className="group flex items-center gap-4 rounded-2xl border bg-card p-5 shadow-sm
                         hover:border-[#0A66C2]/40 transition-colors"
            >
              <span className="flex size-12 shrink-0 items-center justify-center rounded-xl
                               bg-[#0A66C2] text-white">
                <LinkedInIcon className="size-6" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block font-semibold text-foreground">
                  Folgen Sie mir auf LinkedIn
                </span>
                <span className="block text-sm text-muted-foreground leading-relaxed">
                  Mein beruflicher Werdegang und Netzwerk finden Sie unter{" "}
                  <span className="font-medium text-[#0A66C2] dark:text-[#5AA9F0]">{LINKEDIN_HANDLE}</span>
                </span>
              </span>
              <ArrowUpRight className="size-5 shrink-0 text-muted-foreground
                                       group-hover:text-[#0A66C2] transition-colors" />
            </a>
            <a
              data-testid="mastodon-link"
              href={MASTODON_URL}
              rel="me noopener"
              target="_blank"
              className="group flex items-center gap-4 rounded-2xl border bg-card p-5 shadow-sm
                         hover:border-[#6364FF]/40 transition-colors"
            >
              <span className="flex size-12 shrink-0 items-center justify-center rounded-xl
                               bg-[#6364FF] text-white">
                <MastodonIcon className="size-6" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block font-semibold text-foreground">
                  Folgen Sie mir auf Mastodon
                </span>
                <span className="block text-sm text-muted-foreground leading-relaxed">
                  Wer mir und meinen Projekten folgen möchte, findet mich unter{" "}
                  <span className="font-medium text-[#6364FF] dark:text-[#8C8DFF]">{MASTODON_HANDLE}</span>
                </span>
              </span>
              <ArrowUpRight className="size-5 shrink-0 text-muted-foreground
                                       group-hover:text-[#6364FF] transition-colors" />
            </a>
          </div>

          {/* Right – form */}
          <form
            data-testid="contact-form"
            onSubmit={handleSubmit}
            noValidate
            className="space-y-4"
          >
            {/* Name */}
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">
                Wie lautet Ihr Name?
              </label>
              <input
                type="text"
                data-testid="contact-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ihr Name"
                className={fieldCls("name")}
              />
              {errors.name && <p data-testid="error-name" className="text-destructive text-xs mt-1">{errors.name}</p>}
            </div>

            {/* Email */}
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">
                Wie lautet Ihre E-Mail-Adresse?
              </label>
              <input
                type="email"
                data-testid="contact-email-input"
                value={form.email}
                onChange={(e) => {
                  setForm({ ...form, email: e.target.value });
                  if (errors.contact || errors.email)
                    setErrors((p) => ({ ...p, contact: undefined, email: undefined }));
                }}
                placeholder="ihre@email.ch"
                className={fieldCls("email")}
              />
              {errors.email && <p data-testid="error-email" className="text-destructive text-xs mt-1">{errors.email}</p>}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">
                Unter welcher Telefonnummer kann ich Sie erreichen?
              </label>
              <input
                type="tel"
                data-testid="contact-phone-input"
                value={form.phone}
                onChange={(e) => {
                  setForm({ ...form, phone: e.target.value });
                  if (errors.contact) setErrors((p) => ({ ...p, contact: undefined }));
                }}
                placeholder="+41 79 000 00 00"
                className={fieldCls("phone")}
              />
            </div>

            {errors.contact
              ? <p data-testid="error-contact" className="text-destructive text-xs">{errors.contact}</p>
              : <p data-testid="contact-hint" className="text-muted-foreground/60 text-xs">Bitte hinterlassen Sie mindestens eine Kontaktmöglichkeit.</p>
            }

            {/* Message */}
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">
                Weitere Informationen oder Fragen?
              </label>
              <textarea
                data-testid="contact-message-input"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Beschreiben Sie kurz Ihr Anliegen..."
                rows={4}
                className={`${fieldCls("message")} resize-none`}
              />
              {errors.message && <p data-testid="error-message" className="text-destructive text-xs mt-1">{errors.message}</p>}
            </div>

            {/* Turnstile */}
            <div data-testid="turnstile-widget" className="flex justify-center">
              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITEKEY}
                onSuccess={(t) => { setToken(t); setErrors((p) => ({ ...p, captcha: undefined })); }}
                onError={() => { setToken(""); setErrors((p) => ({ ...p, captcha: "CAPTCHA-Fehler. Bitte Seite neu laden." })); }}
                onExpire={() => { setToken(""); setErrors((p) => ({ ...p, captcha: "CAPTCHA abgelaufen. Bitte erneut bestätigen." })); }}
                options={{ theme: "auto", language: "de" }}
              />
            </div>
            {errors.captcha && (
              <p data-testid="error-captcha" className="text-destructive text-xs text-center">{errors.captcha}</p>
            )}

            {/* Submit */}
            <button
              type="submit"
              data-testid="contact-submit-btn"
              disabled={status === "loading" || !turnstileToken}
              className="w-full flex items-center justify-center gap-2 rounded-full
                         bg-accent text-accent-foreground px-6 py-3.5 text-sm font-semibold
                         hover:opacity-90 transition-opacity disabled:opacity-40"
            >
              {status === "loading" ? "Wird gesendet…" : (
                <><Send className="size-4" strokeWidth={2} /> Nachricht absenden</>
              )}
            </button>

            {status === "success" && (
              <motion.div
                data-testid="contact-success-message"
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className="flex flex-col items-center text-center gap-1"
              >
                <SuccessAnimation />
                <p className="text-accent text-sm font-semibold">
                  Vielen Dank! Ich melde mich bald bei Ihnen.
                </p>
              </motion.div>
            )}

            {status === "error" && (
              <motion.p
                data-testid="contact-error-message"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="text-destructive text-center text-sm"
              >
                Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.
              </motion.p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
